import { useState } from "react";
import { Home, Search, ArrowLeft, HelpCircle, LayoutDashboard } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import ComingSoonModal from "../components/ComingSoonModal";

export default function NotFound() {
  const location = useLocation();
  const isAdminPath = location.pathname.startsWith("/admin");

  const [comingSoonModal, setComingSoonModal] = useState({
    isOpen: false,
    featureName: "",
  });

  const handleComingSoon = (e, feature) => {
    e.preventDefault();
    setComingSoonModal({ isOpen: true, featureName: feature });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#121212] flex items-center justify-center px-6 py-12">
      <div className="max-w-2xl w-full text-center">
        {/* 404 Number with gradient */}
        <div className="mb-8 relative">
          <h1
            className="font-medium font-serif bg-gradient-to-br from-[#62397f] to-[#7C3AED] bg-clip-text text-transparent"
            style={{
              fontSize: "clamp(80px, 20vw, 180px)",
              lineHeight: "1",
            }}
          >
            404
          </h1>
          <div className="absolute inset-0 bg-gradient-to-br from-[#62397f]/20 to-[#7C3AED]/20 blur-3xl -z-10" />
        </div>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-gradient-to-br from-[#62397f] to-[#7C3AED] rounded-full flex items-center justify-center shadow-lg">
            <HelpCircle size={40} className="text-white" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-3xl md:text-4xl font-bold text-black dark:text-white mb-4">
          Page Not Found
        </h2>

        {/* Description */}
        <p className="text-lg text-[#666666] dark:text-[#A0A0A0] mb-8 max-w-md mx-auto">
          Oops! The page you're looking for seems to have wandered off. Let's
          get you back on track.
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          {isAdminPath ? (
            <Link
              to="/admin/dashboard/"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#62397f] to-[#7C3AED] hover:from-[#7C3AED] hover:to-[#6D28D9] text-white font-semibold rounded-xl transition-all duration-200 shadow-[0_4px_16px_rgba(139,92,246,0.4)]"
            >
              <LayoutDashboard size={20} />
              <span>Back to Dashboard</span>
            </Link>
          ) : (
            <Link
              to="/"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#62397f] to-[#7C3AED] hover:from-[#7C3AED] hover:to-[#6D28D9] text-white font-semibold rounded-xl transition-all duration-200 shadow-[0_4px_16px_rgba(139,92,246,0.4)]"
            >
              <Home size={20} />
              <span>Back to Home</span>
            </Link>
          )}
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-3 px-8 py-4 bg-white dark:bg-[#2A2A2A] border-2 border-[#E9E9E9] dark:border-[#333333] text-black dark:text-white font-semibold rounded-xl hover:border-[#62397f] dark:hover:border-[#62397f] transition-all duration-200"
          >
            <ArrowLeft size={20} />
            <span>Go Back</span>
          </button>
        </div>

        {/* Helpful Links - Only show for non-admin paths */}
        {!isAdminPath && (
          <div className="pt-8 border-t border-[#E9E9E9] dark:border-[#333333]">
            <p className="text-sm text-[#666666] dark:text-[#A0A0A0] mb-4">
              Looking for something specific?
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/rentals"
                className="text-sm font-medium text-[#62397f] hover:text-[#7C3AED] transition-colors"
              >
                Browse Rentals
              </Link>
              <span className="text-[#E9E9E9] dark:text-[#333333]">•</span>
              <Link
                to="/gallery"
                className="text-sm font-medium text-[#62397f] hover:text-[#7C3AED] transition-colors"
              >
                View Gallery
              </Link>
              <span className="text-[#E9E9E9] dark:text-[#333333]">•</span>
              <Link
                to="/contact"
                className="text-sm font-medium text-[#62397f] hover:text-[#7C3AED] transition-colors"
              >
                Contact Us
              </Link>
              <span className="text-[#E9E9E9] dark:text-[#333333]">•</span>
              <Link
                to="#"
                onClick={(e) => handleComingSoon(e, "About Page")}
                className="text-sm font-medium text-[#62397f] hover:text-[#7C3AED] transition-colors"
              >
                About Us
              </Link>
            </div>
          </div>
        )}

        {/* Decorative elements */}
        <div className="fixed top-20 left-10 w-32 h-32 bg-[#62397f]/5 rounded-full blur-3xl -z-10" />
        <div className="fixed bottom-20 right-10 w-40 h-40 bg-[#7C3AED]/5 rounded-full blur-3xl -z-10" />
      </div>

      <ComingSoonModal
        isOpen={comingSoonModal.isOpen}
        onClose={() => setComingSoonModal({ isOpen: false, featureName: "" })}
        featureName={comingSoonModal.featureName}
      />
    </div>
  );
}
